import { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Custom hook to fetch and manage technologists list
 * Returns technologists from the same facility as the current user
 * Falls back to just the current user's name if no facility is assigned
 */
export function useTechnologists() {
  const [technologists, setTechnologists] = useState([]);
  const [defaultTech, setDefaultTech] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchTechnologists = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found. Please log in again.');
        }
        
        // Use new dedicated technologists endpoint
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const response = await axios.get(`${apiUrl}/api/profile/technologists`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data && response.data.success) {
          // Server returns: { technologists: ['Name1', 'Name2'], defaultTech: 'Name1' }
          const techList = response.data.technologists || [];
          
          // Always add "Other" option at the end
          if (!techList.includes('Other')) {
            techList.push('Other');
          }
          
          setTechnologists(techList);
          setDefaultTech(response.data.defaultTech || '');
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err) {
        console.error('Error fetching technologists:', err);
        setError(err.message || 'Failed to load technologists');
        
        // Fallback to basic list on error
        try {
          const userInfo = JSON.parse(localStorage.getItem('userInfo'));
          if (userInfo && userInfo.fullName) {
            setTechnologists([userInfo.fullName, 'Other']);
            setDefaultTech(userInfo.fullName);
          } else {
            setTechnologists(['Other']);
            setDefaultTech('');
          }
        } catch (fallbackErr) {
          setTechnologists(['Other']);
          setDefaultTech('');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchTechnologists();
  }, []);
  
  return { technologists, defaultTech, loading, error };
}
